import { Spinner } from "@/components/ui/spinner";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { CHART_COLORS } from "./chartTokens";
import { formatDecimal, formatInteger } from "@/utils";

type FunnelData = {
  merchantId: string;
  totalLeads: number;
  totalDelivered: number;
  totalRead: number;
  totalResponded: number;
  totalAppointmentIntention: number;
  totalAppointments: number;
  totalTransfers: number;
  totalDiscarded: number;
  totalInactive: number;
  totalLeadsPercentage: number;
  deliveredPercentage: number;
  readPercentage: number;
  respondedPercentage: number;
  appointmentIntentionPercentage: number;
  appointmentsPercentage: number;
  transfersPercentage: number;
  discardedPercentage: number;
  inactivePercentage: number;
};

type ProjectData = {
  id: string;
  name: string;
};

type CampaignRuleData = {
  id: string;
  name: string;
};

type FunnelCardProps = {
  data: FunnelData | null;
  projects: ProjectData[];
  leadDirection: "all" | "inbound" | "inbound-email" | "outbound" | "pauta";
  selectedProject: string;
  onDirectionChange: (
    direction: "all" | "inbound" | "inbound-email" | "outbound" | "pauta"
  ) => void;
  onProjectChange: (projectId: string) => void;
  campaignRules?: CampaignRuleData[];
  selectedCampaign?: string;
  onCampaignChange?: (campaign: string) => void;
  colSpan?: number;
  isLoading?: boolean;
};

type ChartDataItem = {
  name: string;
  value: number;
  nominalValue: number;
  percentage: number;
  color: string;
};

const HorizontalBar = ({ item, maxValue }: { item: ChartDataItem; maxValue: number }) => {
  const barWidth = maxValue === 0 ? 0 : (item.value / maxValue) * 100;

  return (
    <TooltipProvider>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className="mb-3 flex h-[50px] cursor-pointer items-center">
            <div className="w-[80px] pr-4 md:w-[140px]">
              <p className="text-right text-xs font-medium text-palomma-secondary md:text-sm">
                {item.name}
              </p>
            </div>

            <div className="relative flex h-[35px] flex-1 items-center">
              <div className="relative h-[35px] w-full overflow-hidden rounded-md bg-muted">
                <div
                  className="relative flex h-full items-center justify-end rounded-md pr-2 transition-[width] duration-[800ms] ease-out"
                  style={{ width: `${barWidth}%`, backgroundColor: item.color }}
                >
                  {barWidth > 15 && (
                    <span className="text-xs font-semibold text-white">
                      {formatDecimal(item.percentage, 1)}%
                    </span>
                  )}
                </div>
              </div>

              {barWidth <= 15 && (
                <span
                  className="absolute ml-2 text-xs font-medium text-muted-foreground"
                  style={{
                    left: `${barWidth + 2}%`,
                    transform: "translateY(-50%)",
                    top: "50%",
                  }}
                >
                  {formatDecimal(item.percentage, 1)}%
                </span>
              )}

              <div className="ml-4 min-w-[50px] md:min-w-[80px]">
                <p className="text-sm font-medium text-palomma-secondary">
                  {formatInteger(item.nominalValue)}
                </p>
              </div>
            </div>
          </div>
        </TooltipTrigger>
        <TooltipContent>
          <div>
            <p className="text-sm font-semibold">{item.name}</p>
            <p className="text-sm">{formatInteger(item.nominalValue)} contactos</p>
            <p className="text-sm">{formatDecimal(item.percentage, 1)}% del total</p>
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

const FunnelCard = ({
  data,
  projects,
  leadDirection,
  selectedProject,
  onDirectionChange,
  onProjectChange,
  campaignRules,
  selectedCampaign,
  onCampaignChange,
  colSpan = 1,
  isLoading = false,
}: FunnelCardProps) => {
  if (isLoading) {
    return (
      <div
        className="h-[300px] md:h-[400px]"
        style={{ gridColumn: `span ${colSpan}` }}
      >
        <div className="flex items-center justify-center h-full">
          <Spinner size="xl" />
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div
        className="h-[300px] md:h-[400px]"
        style={{ gridColumn: `span ${colSpan}` }}
      >
        <div>
          <p>No hay datos disponibles</p>
        </div>
      </div>
    );
  }

  // Transform data for horizontal bars - ordered from top to bottom like a funnel
  // Calculate the width of bars relative to the maximum value for visual representation
  const calculateValue = (value: number) => {
    return value; // Use actual values for bar width calculation
  };

  // Calculate percentage relative to total leads (the top of the funnel)
  const calculatePercentage = (value: number) => {
    if (data.totalLeads === 0) return 0;
    return (value / data.totalLeads) * 100;
  };

  const chartData: ChartDataItem[] = [
    {
      name: "Contactos alcanzables",
      value: calculateValue(data.totalLeads),
      nominalValue: data.totalLeads,
      percentage: 100,
      color: CHART_COLORS.primary,
    },
    {
      name: "Entregados",
      value: calculateValue(data.totalDelivered),
      nominalValue: data.totalDelivered,
      percentage: calculatePercentage(data.totalDelivered),
      color: CHART_COLORS.primary,
    },
    {
      name: "Leyeron",
      value: calculateValue(data.totalRead),
      nominalValue: data.totalRead,
      percentage: calculatePercentage(data.totalRead),
      color: CHART_COLORS.primary,
    },
    {
      name: "Respondieron",
      value: calculateValue(data.totalResponded),
      nominalValue: data.totalResponded,
      percentage: calculatePercentage(data.totalResponded),
      color: CHART_COLORS.primary,
    },
    {
      name: "Intención de cita",
      value: calculateValue(data.totalAppointmentIntention),
      nominalValue: data.totalAppointmentIntention,
      percentage: calculatePercentage(data.totalAppointmentIntention),
      color: CHART_COLORS.primary,
    },
    {
      name: "Agendaron",
      value: calculateValue(data.totalAppointments),
      nominalValue: data.totalAppointments,
      percentage: calculatePercentage(data.totalAppointments),
      color: CHART_COLORS.primary,
    },
    {
      name: "Transferidos",
      value: calculateValue(data.totalTransfers),
      nominalValue: data.totalTransfers,
      percentage: calculatePercentage(data.totalTransfers),
      color: CHART_COLORS.primary,
    },
    {
      name: "Descartados",
      value: calculateValue(data.totalDiscarded),
      nominalValue: data.totalDiscarded,
      percentage: calculatePercentage(data.totalDiscarded),
      color: CHART_COLORS.destructive,
    },
    {
      name: "Inactivos",
      value: calculateValue(data.totalInactive),
      nominalValue: data.totalInactive,
      percentage: calculatePercentage(data.totalInactive),
      color: CHART_COLORS.neutral,
    },
  ];

  const maxValue = Math.max(...chartData.map((item) => item.value));

  const getProjectDisplayName = (projectName: string) => {
    return projectName.length > 20 ? `${projectName.substring(0, 20)}...` : projectName;
  };

  const directionLabel =
    leadDirection === "all"
      ? "Todos"
      : leadDirection === "pauta"
        ? "Pauta"
        : leadDirection === "inbound-email"
          ? "Inbound Email"
          : leadDirection.charAt(0).toUpperCase() + leadDirection.slice(1);

  return (
    <div style={{ gridColumn: `span ${colSpan}` }}>
      <div className="h-full rounded-2xl border border-border bg-card p-6">
        <div className="flex flex-col items-stretch gap-4">
          <div className="flex flex-col items-stretch gap-3">
            <div className="flex flex-col items-stretch justify-between gap-2 md:flex-row md:items-center">
              <h3 className="text-sm font-semibold text-palomma-secondary">
                Funnel WhatsApp — {directionLabel}
                {selectedProject !== "all" && (
                  <span className="font-normal text-muted-foreground">
                    {" · "}
                    {getProjectDisplayName(
                      projects.find((p) => p.id === selectedProject)?.name ||
                        projects.find((p) => p.name === selectedProject)?.name ||
                        selectedProject
                    )}
                  </span>
                )}
              </h3>
              <Select
                value={leadDirection}
                onValueChange={(value) =>
                  onDirectionChange(
                    value as "all" | "inbound" | "inbound-email" | "outbound" | "pauta"
                  )
                }
              >
                <SelectTrigger className="h-11 w-full md:h-10 md:w-[160px]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="inbound">Inbound</SelectItem>
                  <SelectItem value="inbound-email">Inbound Email</SelectItem>
                  <SelectItem value="outbound">Outbound</SelectItem>
                  <SelectItem value="pauta">Pauta</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {projects.length > 0 && (
              <div className="flex flex-col items-stretch justify-between gap-2 md:flex-row md:items-center">
                <p className="text-sm font-medium text-muted-foreground">
                  Filtrar por proyecto
                </p>
                <Select
                  value={selectedProject || "all"}
                  onValueChange={onProjectChange}
                >
                  <SelectTrigger className="h-11 w-full md:h-10 md:w-[300px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los proyectos</SelectItem>
                    {projects.map((project) => (
                      <SelectItem key={project.id} value={project.id}>
                        {project.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {leadDirection === "pauta" && campaignRules && campaignRules.length > 0 && onCampaignChange && (
              <div className="flex flex-col items-stretch justify-between gap-2 md:flex-row md:items-center">
                <p className="text-sm font-medium text-muted-foreground">
                  Filtrar por pauta
                </p>
                <Select
                  value={selectedCampaign || "all"}
                  onValueChange={onCampaignChange}
                >
                  <SelectTrigger className="h-11 w-full md:h-10 md:w-[300px]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todas las pautas</SelectItem>
                    {campaignRules.map((rule) => (
                      <SelectItem key={rule.id} value={rule.name}>
                        {rule.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>

          <div className="py-2">
            {chartData.map((item, index) => (
              <HorizontalBar key={index} item={item} maxValue={maxValue} />
            ))}
          </div>

          <p className="mt-2 text-center text-xs text-muted-foreground">
            Porcentaje de contactos por etapa del funnel
          </p>
        </div>
      </div>
    </div>
  );
};

export default FunnelCard;
